/* eslint no-underscore-dangle: 0 */
/* eslint import/no-extraneous-dependencies: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';

function HtmlTemplateThirty({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [
    openContentUploadConfirmation,
    setOpenContentUploadConfirmation,
  ] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
    @font-face {
      font-family: 'AlfaSlabOne';
      src: url(../../../assets/fonts/AlfaSlabOne/AlfaSlabOne-Regular.ttf) format('truetype');
      font-weight: 400;
      font-style: normal;
    }
    
    .SpecialOffer {
      background-image: url("../../../assets/images/tshirt.png"),
        /* outline pattern */
        url("../../../assets/images/background30.jpg"); /* paper texture */
      background-repeat: repeat, repeat;
      background-size: 120px auto, cover; /* adjust scale of tshirt */
    }
    
    .SpecialOffer .offer-container {
      text-align: center;
      padding: 40px;
    }
    
    .SpecialOffer .offer-container .offer-title {
      font-size: 60px;
      font-weight: bold;
      font-family: "AlfaSlabOne", serif;
      letter-spacing: 3px;
      text-transform: uppercase;
    }
    
    .SpecialOffer .offer-container .offer-details {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      margin: 40px auto;
      position: relative;
      flex-direction: row;
      gap: 70px;
    }
    
    .SpecialOffer .offer-container .offer-details .offer-date {
      background: #fff;
      border-radius: 6px;
      padding: 10px 20px;
      font-weight: 700;
      color: #b61b1b;
      border: 2px solid #fff;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      font-family: "Arial", "Helvetica", sans-serif;
    }
    
    .SpecialOffer .offer-container .offer-details .tshirts {
      display: flex;
      gap: 20px;
      justify-content: center;
      align-items: flex-end;
      margin: 30px 0;
      width: 50%;
    }
    
    .SpecialOffer .offer-container .offer-details .tshirts img {
      object-fit: contain;
    }
    
    .SpecialOffer .offer-container .offer-details .discount-badge {
      color: #fff;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-weight: bold;
      font-size: 18px;
      text-transform: uppercase;
      margin-top: 200px;
      font-family: "Arial", "Helvetica", sans-serif;
    }
    
    .SpecialOffer .offer-container .offer-details .discount-badge p {
      font-size: 35px;
    }
    
    .SpecialOffer .offer-container .offer-footer {
      display: flex;
      justify-content: space-between;
      max-width: 1000px;
      margin: auto;
      font-weight: bold;
      align-items: center;
      font-family: "Arial", "Helvetica", sans-serif;
    }
    
    .SpecialOffer .offer-container .offer-footer .visit-btn {
      background: #b61b1b;
      color: #fff;
      border: none;
      padding: 15px 40px;
      font-size: 20px;
      font-weight: bold;
      margin: 30px 0;
      border-radius: 6px;
      cursor: pointer;
      transition: 0.3s;
    }
    
    .SpecialOffer .offer-container .offer-footer .visit-btn:hover {
      background: #d62525;
    }
    
    .SpecialOffer .offer-container .offer-footer .phone,
    .SpecialOffer .offer-container .offer-footer .website {
      color: #b61b1b;
    }     
  `;

  const convertImage = async (url) => {
    await utils
      .convertSignedUrl(url)
      .then((response) => {
        setDisable(true);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertBase64 = async () => {
    toPng(htmlRef?.current, { cacheBust: true })
      .then((dataUrl) => {
        setBase64(dataUrl);
        convertImage(dataUrl);
      })
      .catch((error) => {
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertHtml = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => setData(res));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      /* used settimeout the page was fully rendered and then called the api so wait for the response */
      setTimeout(() => {
        convertBase64();
      }, 1000);
      convertHtml();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: (
        <TemplateContentUpload
          base64={base64}
          setNotification={setNotification}
          fileType={fileType}
          htmlContent={htmlContent}
        />
      ),
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: (
        <FileFormat
          setNotification={setNotification}
          handleFileType={handleFileType}
          setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
          data={data}
        />
      ),
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundImage = Array.isArray(data?.[0]?.template_backgroundImage_url)
    ? data?.[0]?.template_backgroundImage_url
    : [];
  const styles = data?.[0]?.styles?.details;
  // Animation variants for each letter
  const letterAnimation = {
    hidden: { y: 100, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1, // stagger effect
        duration: 0.6,
        ease: 'easeOut',
      },
    }),
  };
  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={!disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: (
            <PopupBox
              setOpenContentUploadConfirmation={
                setOpenContentUploadConfirmation
              }
              setNotification={setNotification}
            />
          ),
        }}
      />

      <div className="header-height" />

      <div ref={htmlRef} className="SpecialOffer">
        <div className="offer-container">
          <h1
            className="offer-title"
            style={{ color: styles?.[0]?.color, fontSize: styles?.[0]?.fontSize }}
          >
            {contentDetails?.[0]?.header?.split('').map((char, i) => (
              <motion.span
                key={Math.random()}
                custom={i}
                initial="hidden"
                animate="visible"
                variants={letterAnimation}
                className="offer-title"
                style={{ color: styles?.[0]?.color, fontSize: styles?.[0]?.fontSize }}
              >
                {char === ' ' ? '\u00A0' : char}
              </motion.span>
            ))}
          </h1>

          <div className="offer-details">
            <motion.p
              className="offer-date"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              style={{ color: styles?.[0]?.color, fontSize: styles?.[1]?.fontSize }}
            >
              {contentDetails?.[0]?.item_name}
            </motion.p>
            <motion.div
              className="tshirts"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              <img src={contentDetails?.[0]?.image} alt="White T-shirt" />
            </motion.div>
            <motion.div
              className="discount-badge"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 120, delay: 0.6 }}
              style={{
                backgroundImage: `url(${backgroundImage?.[1]})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                height: '245px',
                width: '245px',
              }}
            >
              <p>{contentDetails?.[0]?.offer_percentage}</p>
            </motion.div>
          </div>
          <motion.div
            className="offer-footer"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <p
              className="phone"
              style={{
                color: styles?.[0]?.color,
                fontSize: styles?.[2]?.fontSize,
              }}
            >
              {contentDetails?.[0]?.description}
            </p>
            <motion.button
              className="visit-btn"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              VISIT NOW
            </motion.button>
            <p
              className="website"
              style={{
                color: styles?.[0]?.color,
                fontSize: styles?.[2]?.fontSize,
              }}
            >
              {contentDetails?.[0]?.website}
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

HtmlTemplateThirty.propTypes = {
  templateData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    template_title: PropTypes.string.isRequired,
  }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateThirty;
